import React, { useState, useEffect, useRef } from "react";
import { Skeleton } from "@mui/material";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import ViewAvailable from "./ViewAvailable";
import ReactPaginate from "react-paginate";
import { usePermission } from "../../../context/PermissionContext";
import Messages from "../../../constants/Messages";
import useKeyboardTableNavigation from "../../../constants/useKeyboardTableNavigation";
import Table from "../../../constants/Table";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import ProductSearchList from "../../../constants/ProductSearchList";
import { LoaderAltIcon, RefreshIcon, SearchIcon, ShowIcon, XIcon } from "../../../constants/Icons";

const StockAvailable = ({ isActive }) => {
  const [isPreLoading, setPreLoading] = useState(true);
  const [isBtnLoading, setBtnLoading] = useState({});
  const [isStockData, setStockData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isStockModalOpen, setStockModalOpen] = useState(false);
  const [isViewData, setStockViewData] = useState(null);
  const containerRef = useRef(null);
  const [isSearchTerm, setSearchTerm] = useState("");
  const [ProductSuggestions, setProductSuggestions] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedProductCode, setSelectedProductCode] = useState("");

  const searchInputRef = useRef(null);
  const ProductSuggestionsRef = useRef(null);
  const suggestionRefs = useRef([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const perPage = 10;

  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { permission, hasPermission, globelPopupOpen } = usePermission();
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);

  const handleSearch = async (term) => {
    if (hasPermission("STOCK", "PRODUCT:search") || permission === undefined) {
      setSearchTerm(term);
      setHasSearched(true);

      if (!term.trim()) {
        setProductSuggestions([]);
        await handleAvailableList(0, "");
        setCurrentPage(0);
        return;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      debounceTimerRef.current = setTimeout(async () => {
        try {
          if (!checkNetwork()) return;
          setSuggestionLoading(true);
          controllerRef.current = new AbortController();

          const response = await AxiosInstance.get(
            `module/product/search-hybrid-active?searchtxt=${term}`,
            { signal: controllerRef.current.signal },
          );

          const results = response.data.results;
          setProductSuggestions(results || []);
        } catch (error) {
          if (error.name !== "CanceledError" && error.name !== "AbortError") {
            handleError(error, showAlert);
          }
        } finally {
          setSuggestionLoading(false);
        }
      }, 200);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleSearchFind = () => {
    const currentInputValue = searchInputRef.current?.value || "";
    setSearchTerm(currentInputValue);
    setSelectedProductCode(currentInputValue);
    if (!currentInputValue.trim()) {
      handleAvailableList(0, "");
    } else {
      handleAvailableList(0, currentInputValue);
    }
    setProductSuggestions([]);
    setHasSearched(false);
    setCurrentPage(0);
  };

  const handleSelectProduct = (product) => {
    const code = product.code;
    setSearchTerm(code);
    setProductSuggestions([]);
    setSelectedProductCode(code);
    setHasSearched(false);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    handleAvailableList(0, code);
    setCurrentPage(0);
  };

  const handleOutsideClick = (e) => {
    if (
      ProductSuggestionsRef.current &&
      !ProductSuggestionsRef.current.contains(e.target)
    ) {
      setProductSuggestions([]);
      setHasSearched(false);
      setSearchTerm("");
    }
  };

  const handleAvailableList = async (page = 0, term = "") => {
    setPreLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/stock/list/available?pageno=${
          page + 1
        }&limit=${perPage}&searchtxt=${term}`,
      );
      const results = response.data.results;
      setStockData(results || []);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalItems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPreLoading(false);
    }
  };

  const handleViewStock = async (item) => {
    setBtnLoading((prev) => ({ ...prev, [item._id]: true }));
    try {
      if (!checkNetwork()) return;
      const data = { productid: item._id };
      const response = await AxiosInstance.get(`module/stock/${item._id}`);
      setStockModalOpen(true);
      setStockViewData(response.data.results);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading((prev) => ({ ...prev, [item._id]: false }));
    }
  };

  const handleStockUpdate = (productid, quantity) => {
    console.log(productid, quantity);
    setStockData((prevData) =>
      prevData.map((item) =>
        item._id === productid
          ? { ...item, totalStockLeft: item.totalStockLeft - quantity }
          : item,
      ),
    );
  };

  const handleClose = () => {
    setStockModalOpen(false);
    setStockViewData(null);
  };

  // useEffect(() => {
  //     handleAvailableList();
  // }, []);

  const handleSuggestionNavigation = (e) => {
    if (e.key === "Enter") {
      if (activeIndex >= 0 && ProductSuggestions[activeIndex]) {
        e.preventDefault();
        handleSelectProduct(ProductSuggestions[activeIndex]);
      } else {
        e.preventDefault();
        handleSearchFind();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < ProductSuggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : ProductSuggestions.length - 1,
      );
    }
  };

  useKeyboardTableNavigation({
    isActive,
    containerRef,
    searchTerms: isSearchTerm,
    suggestions: ProductSuggestions,
    modals: {
      isStockModalOpen,
      globelPopupOpen,
    },
    onEnter: handleViewStock,
  });

  useEffect(() => {
    if (!isActive) return;

    // small timeout ensures DOM is visible after tab switch
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isActive]);

  useEffect(() => {
    if (activeIndex >= 0 && suggestionRefs.current[activeIndex]) {
      suggestionRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    handleAvailableList(currentPage, isSearchTerm);
  }, [currentPage]);

  const handleRefresh = async () => {
    await handleAvailableList(currentPage, isSearchTerm);
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="page-content">
          <div className="page-breadcrumb d-flex align-items-center mb-3 gap-2">
            <div className="pb-1">
              <button
                type="button"
                className="btn btn-sm btn-light p-1 shadow-none"
                onClick={handleRefresh}
                disabled={isPreLoading}
                title="Refresh list"
              >
                <RefreshIcon iClass={`${isPreLoading ? "bx-spin" : ""}`}/>
              </button>
            </div>
            <div className="breadcrumb-title pe-3">Stock Available</div>
          </div>

          <div className="row">
            <div className="col">
              <div className="card radius-10 mb-0">
                <div className="card-body px-0">
                  <div className="row px-3">
                    <div className="col-lg-5 me-auto">
                      <div className="ms-auto position-relative">
                        <div
                          className="input-group"
                          ref={ProductSuggestionsRef}
                        >
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search product by code, title, etc..."
                            ref={searchInputRef}
                            value={selectedProductCode}
                            onChange={(e) => {
                              handleSearch(e.target.value);
                              setSelectedProductCode(e.target.value);
                              setActiveIndex(-1);
                            }}
                            onKeyDown={handleSuggestionNavigation}
                            autoComplete="off"
                            autoFocus 
                          />
                         {selectedProductCode ? (
                              <span
                                className="input-group-text"
                                onClick={() => {
                                  setSelectedProductCode("");
                                  handleAvailableList();
                                  setSearchTerm("")
                                }}
                              >
                                <XIcon/>
                              </span>
                            ) : (
                              <button
                                className="input-group-text"
                                onClick={handleSearchFind}
                              >
                                <SearchIcon/>
                              </button>
                            )}
                        </div>
                        {hasSearched &&
                          isSearchTerm &&
                          !suggestionLoading &&
                          ProductSuggestions.length === 0 && (
                            <ul className="suggestions-list">
                              <li className="suggestion-item text-muted">
                                No products data was found
                              </li>
                            </ul>
                          )}

                        {ProductSuggestions.length > 0 && (
                          <ProductSearchList suggest={ProductSuggestions}
                          suggestionRefs={suggestionRefs}
                           handleSelect={handleSelectProduct} activeIndex={activeIndex}
                           />
                        )}
                      </div>
                    </div>
                  </div>

                  <div ref={containerRef}>
                    <Table
                      loading={isPreLoading}
                      data={isStockData}
                      onRowClick={(item) => handleViewStock(item)}
                      columns={[
                        { label: "S.No", render: (_, index) => index + 1 },
                        {
                          label: "SKU Code",
                          render: (item) => (
                            <span className="badge rounded-pill bg-label-navy">
                              {item.code}
                            </span>
                          ),
                        },
                        {
                          label: "HSN/SAC",
                          render: (item) => (
                            <span className="badge rounded-pill bg-label-retro">
                              {item.hsnsac}
                            </span>
                          ),
                        },
                        {
                          label: "Title",
                          render: (item) => (
                            <span className="text-heading fw-medium text-wrap text-left p-2">
                              {item.title}
                            </span>
                          ),
                        },
                        {
                          label: "Total Stock Left",
                          render: (item) => (
                            <div className="no-print position-relative">
                              <div className="d-flex justify-content-center align-items-center">
                            <span className="stock-status-badge badge bg-label-space">
                              {item.totalStockLeft}{" "} {item?.totalStockLeftUnit}
                            </span>
                            <div className="order-actions order-actions3 opacity-0 opacity-md-100">
                              <button
                                type="button"
                                className="btn-label-info shadow-none me-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewStock(item);
                                }}
                              >
                                {isBtnLoading[item._id] ? (
                                  <LoaderAltIcon iClass={"bx-spin"}/>
                                ) : (
                                  <ShowIcon/>
                                )}
                              </button>
                            </div>
                            </div>
                            </div>
                          ),
                        },
                        // {
                        //   label: "Actions",
                        //   render: (item) => (
                        //     <div className="order-actions opacity-0 opacity-md-100">
                        //       <button
                        //         type="button"
                        //         className="btn-label-info shadow-none me-2"
                        //         onClick={(e) => {
                        //           e.stopPropagation();
                        //           handleViewStock(item);
                        //         }}
                        //       >
                        //         {isBtnLoading[item._id] ? (
                        //           <i className="bx bx-loader-alt bx-spin"></i>
                        //         ) : (
                        //           <i className="bx bx-show"></i>
                        //         )}
                        //       </button>
                        //     </div>
                        //   ),
                        // },
                      ]}
                    />
                  </div>
                  {totalPages > 0 && (
                    <div className="d-flex justify-content-center mt-3">
                      <ReactPaginate
                        previousLabel={"← Previous"}
                        nextLabel={"Next →"}
                        breakLabel={"..."}
                        onPageChange={({ selected }) => {
                          setPreLoading(true);
                          setCurrentPage(selected);
                        }}
                        pageRangeDisplayed={5}
                        pageCount={totalPages}
                        containerClassName="pagination"
                        pageClassName="page-item"
                        pageLinkClassName="page-link"
                        previousClassName="page-item"
                        previousLinkClassName="page-link"
                        nextClassName="page-item"
                        nextLinkClassName="page-link"
                        breakClassName="page-item"
                        breakLinkClassName="page-link"
                        activeClassName="active"
                        forcePage={currentPage}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isStockModalOpen && isViewData && (
        <ViewAvailable
          data={isViewData}
          onClose={handleClose}
          onStockUpdate={handleStockUpdate}
        />
      )}
    </div>
  );
};

export default StockAvailable;
